import { clsx } from "clsx";
import { Section } from "@/components/Section/Section";
import "@/components/guide/guide-system.css";
import { formatMskAiringShort } from "@/utils/webinar-schedule";
import "./KitNavbar.css";

export interface KitNavbarProps {
  ctaLabel: string;
  ctaHref: string;
  logoHref?: string;
  /** "light" (default) = sitewide bar. "dark" = translucent-ink bar for a hero that sits directly under the nav. */
  look?: "light" | "dark";
  whenHourMsk?: number;
  id?: string;
}

/**
 * Navbar (`kit-navbar`) — sticky top nav shared by every premium landing:
 * brand logo on the left, a single purple CTA button on the right. Visual
 * vocabulary lives in `guide-system.css` (`.nav`, `.logo`, `.ds-btn`);
 * `KitNavbar.css` only pins the JSON-render wrapper sticky (see its header).
 * Extracted from `GuideNav`. JSON-composable (ADR-0076 kit convention).
 *
 * Migrated onto `<Section as="header">` (ADR-0091 phase 2 batch D) via class
 * passthrough — `padding="none"`/`background="none"` because the nav's
 * vertical rhythm is the bespoke `.nav-inner` fixed height, not the
 * `.section` scale. The row layout rides `Section`'s `containerLayout`
 * (a `Flex` container, ADR-0094); `.nav-inner` keeps only the fixed height.
 */
export function KitNavbar({
  ctaLabel,
  ctaHref,
  logoHref = "/",
  look = "light",
  whenHourMsk,
  id,
}: KitNavbarProps) {
  const whenShort = formatMskAiringShort(whenHourMsk ?? 0);
  return (
    <Section
      as="header"
      id={id}
      padding="none"
      background="none"
      className={clsx("nav", look === "dark" && "nav-dark")}
      containerClassName="wrap nav-inner"
      containerLayout={{ justify: "between", align: "center" }}
      data-surface={look === "dark" ? "dark" : undefined}
      data-component="KitNavbar"
    >
      <a href={logoHref} className="logo">
        Лена <i>про карьеру</i>
      </a>
      <div className="nav-right">
        {whenHourMsk !== undefined && (
          <span className="nav-date">
            <span className="nav-live-dot" aria-hidden="true" />
            {whenShort}
          </span>
        )}
        <a href={ctaHref} className="ds-btn ds-btn-action" data-track="guide-nav.cta.primary">
          {ctaLabel}
        </a>
      </div>
    </Section>
  );
}

KitNavbar.displayName = "KitNavbar";
